import { CheckIcon, MinusIcon } from "@heroicons/react/outline";
import { useEffect } from "react";
import type { ReactNode } from "react";
import type { UseFormSetValue, UseFormWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { INewProjectGroup } from "../../projectGroups/state/projectGroupsSlice";
import AccessTypeCustomInput from "./AccessTypeCustomInput";
import "./AccessTypeTable.scss";

interface AccessTypeFeatureRowProps {
  label: ReactNode;
  values: [boolean, boolean, boolean];
  labelHasBottomBorder?: boolean;
}

const AccessTypeFeatureRow = ({
  label,
  values,
  labelHasBottomBorder = true,
}: AccessTypeFeatureRowProps) => (
  <tr>
    <td className={`py-1 ${labelHasBottomBorder ? "border-b" : ""}`}>{label}</td>
    {values.map((value, index) => (
      <td key={index} className="py-1 border-b">
        <div className="flex justify-center">
          {value ? (
            <CheckIcon className="h-5 w-5 stroke-green-500" />
          ) : (
            <MinusIcon className="h-5 w-5 stroke-gray-300" />
          )}
        </div>
      </td>
    ))}
  </tr>
);

const AccessTypeTable = ({
  setValue,
  watch,
}: {
  setValue: UseFormSetValue<INewProjectGroup>;
  watch: UseFormWatch<INewProjectGroup>;
}) => {
  const { t } = useTranslation();
  const selected = watch("accessType");

  const isAnonymousSelected = selected === "none";

  const isSimpSelected = selected === "simplified";

  const isStandardSelected = selected === "classic";

  useEffect(() => {
    setValue("accessType", "none");
  }, []);

  return (
    <table className="relative table-fixed w-full max-w-7xl px-6 xl:px-0">
      <thead>
        <tr>
          <th className="text-left"></th>
          <th className={`text-center py-2 ${isAnonymousSelected ? "active-top" : ""}`}>
            {t("pages.accessTypeTable.noLogin")}
          </th>
          <th className={`text-center py-2 ${isSimpSelected ? "active-top" : ""}`}>
            {t("pages.accessTypeTable.simplifiedLogin")}{" "}
          </th>
          <th className={`text-center py-2 ${isStandardSelected ? "active-top" : ""}`}>
            {t("pages.accessTypeTable.passwordLogin")}{" "}
          </th>
        </tr>
      </thead>
      <thead>
        <tr>
          <th className="text-left bg-white"> {t("pages.accessTypeTable.features")}</th>
          <th
            className={`text-left bg-white py-2 ${isAnonymousSelected ? "active-middle" : ""}`}
          ></th>
          <th className="text-left bg-white"></th>
          <th className="text-left bg-white"></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="py-1 border-b"> {t("pages.accessTypeTable.canUseCollabMode")}</td>
          <td className="py-1 border-b ">
            <div className="flex justify-center">
              <MinusIcon className="h-5 w-5 stroke-gray-300" />
            </div>
          </td>
          <td className="py-1 border-b">
            <div className="flex justify-center">
              <CheckIcon className="h-5 w-5 stroke-green-500" />
            </div>
          </td>
          <td className="py-1 border-b">
            <div className="flex justify-center">
              <CheckIcon className="h-5 w-5 stroke-green-500" />
            </div>
          </td>
        </tr>
        <AccessTypeFeatureRow
          label={t("pages.accessTypeTable.collabAvatarPersonalization")}
          values={[false, false, true]}
        />
        <AccessTypeFeatureRow
          label={t("pages.accessTypeTable.sendMailNotifsToLearners")}
          values={[false, false, true]}
          labelHasBottomBorder={false}
        />
      </tbody>
      <thead>
        <tr>
          <th className="text-left bg-white">{t("pages.accessTypeTable.reporting")}</th>
          <th className="text-left bg-white"></th>
          <th className="text-left bg-white"></th>
          <th className="text-left bg-white"></th>
        </tr>
      </thead>
      <tbody>
        <AccessTypeFeatureRow
          label={t("pages.accessTypeTable.wixarLmsInterfaceForLearners")}
          values={[false, false, true]}
        />
        <AccessTypeFeatureRow
          label={t("pages.accessTypeTable.sendResultsToLearners")}
          values={[false, false, true]}
        />
        <tr>
          <td className="py-1 border-b"> {t("pages.accessTypeTable.identifiedSessions")}</td>
          <td className="py-1 border-b ">
            <div className="flex justify-center">
              <MinusIcon className="h-5 w-5 stroke-gray-300" />
            </div>
          </td>
          <td className="py-1 border-b ">
            <div className="flex justify-center">
              <CheckIcon className="h-5 w-5 stroke-green-500" />
            </div>
          </td>
          <td className="py-1 border-b ">
            <div className="flex justify-center">
              <CheckIcon className="h-5 w-5 stroke-green-500" />
            </div>
          </td>
        </tr>
        <tr>
          <td className="py-1"> {t("pages.accessTypeTable.globalStatsForScorableInteractions")}</td>
          <td className="py-1 border-b ">
            <div className="flex justify-center">
              <CheckIcon className="h-5 w-5 stroke-green-500" />
            </div>
          </td>
          <td className="py-1 border-b ">
            <div className="flex justify-center">
              <CheckIcon className="h-5 w-5 stroke-green-500" />
            </div>
          </td>
          <td className="py-1 border-b ">
            <div className="flex justify-center">
              <CheckIcon className="h-5 w-5 stroke-green-500" />
            </div>
          </td>
        </tr>
      </tbody>
      <thead>
        <tr>
          <th className="text-left bg-white">{t("pages.accessTypeTable.access")}</th>
          <th className="text-left bg-white"></th>
          <th className="text-left bg-white"></th>
          <th className="text-left bg-white"></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="py-1 border-b">{t("pages.accessTypeTable.playableOffline")}</td>
          <td className="py-1 border-b ">
            <div className="flex justify-center">
              <CheckIcon className="h-5 w-5 stroke-green-500" />
            </div>
          </td>
          <td className="py-1 border-b ">
            <div className="flex justify-center">
              <CheckIcon className="h-5 w-5 stroke-green-500" />
            </div>
          </td>
          <td className="py-1 border-b ">
            <div className="flex justify-center">
              <MinusIcon className="h-5 w-5 stroke-gray-300" />
            </div>
          </td>
        </tr>
        <tr>
          <td className="py-1">{t("pages.accessTypeTable.securizedAccessToTheExperience")}</td>

          <td className="py-1 border-b ">
            <div className="flex justify-center">
              <MinusIcon className=" h-5 w-5 stroke-gray-300" />
            </div>
          </td>
          <td className="py-1 text-center border-b w-28">
            <div className="flex justify-center">
              <MinusIcon className="h-5 w-5 stroke-gray-300" />
            </div>
          </td>
          <td className="py-1 text-center  border-b w-28">
            <div className="flex justify-center">
              <CheckIcon className="h-5 w-5 stroke-green-500" />
            </div>
          </td>
        </tr>
        <tr>
          <td className="py-1"></td>

          <td className="py-3 ">
            <div className="flex justify-center">
              <AccessTypeCustomInput
                id="no-login"
                buttonText={t("general.select")}
                setValue={setValue}
                selected={isAnonymousSelected}
              />
            </div>
          </td>
          <td className="py-3">
            <div className="flex justify-center">
              <AccessTypeCustomInput
                id="simplified-login"
                buttonText={t("general.select")}
                setValue={setValue}
                selected={isSimpSelected}
              />
            </div>
          </td>
          <td className="py-3">
            <div className="flex justify-center">
              <AccessTypeCustomInput
                id="password-login"
                buttonText={t("general.select")}
                setValue={setValue}
                selected={isStandardSelected}
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default AccessTypeTable;
